/**
 * Device back handler stack — Android device back / 헤더 뒤로 / 브라우저 back 등을
 * 메시지로 위임받았을 때 호출할 핸들러들의 LIFO 스택.
 *
 * 다층 dismiss (Material/Android 표준): 키보드 → 모달/팝업 → 페이지 단계로 한 칸씩 빠지게 하기 위함.
 *   - 페이지: 마운트 시 push, 언마운트 시 pop
 *   - 모달/BottomSheet: visible 동안 push, 닫힐 때 pop
 *   - 키보드는 시스템이 먼저 처리 → 스택은 키보드 다음 단계부터 담당
 *
 * 본 모듈은 protocol(SET_BACK_INTERCEPT 등) 을 모름. 순수 데이터 구조.
 *   protocol 토글은 wiz-sso-front 의 useBackInterceptor 가 subscribeBackStackChange 로 처리.
 */

import { useEffect, useRef } from "react";

export type BackHandler = () => void;
type ChangeListener = () => void;

const stack: BackHandler[] = [];
const changeListeners: ChangeListener[] = [];

const notify = () => {
    // 복사 후 순회 — listener 내부에서 unsubscribe 가능
    [...changeListeners].forEach((l) => l());
};

/**
 * handler 를 스택에 push. cleanup 함수 반환 (호출 시 동일 handler 를 스택에서 제거).
 *   여러 곳에서 동일 instance 를 push 한 적이 있다면 lastIndexOf 로 가장 최근 것만 제거.
 */
export const pushBackHandler = (handler: BackHandler): (() => void) => {
    stack.push(handler);
    notify();
    let removed = false;
    return () => {
        if (removed) return;
        const idx = stack.lastIndexOf(handler);
        if (idx !== -1) {
            stack.splice(idx, 1);
            removed = true;
            notify();
        }
    };
};

/** 스택 최상위 핸들러 — 없으면 null. */
export const getActiveBackHandler = (): BackHandler | null =>
    stack[stack.length - 1] ?? null;

/** push/pop 발생 시 콜백 호출. unsubscribe 함수 반환. */
export const subscribeBackStackChange = (listener: ChangeListener): (() => void) => {
    changeListeners.push(listener);
    return () => {
        const idx = changeListeners.indexOf(listener);
        if (idx !== -1) changeListeners.splice(idx, 1);
    };
};

/**
 * React hook — handler 가 매 렌더 새 함수여도 ref 동기화로 stale closure 회피.
 *   active=false 면 push 하지 않음 (조건부 활성화용, 예: BottomSheet 의 isVisible).
 */
export const useBackHandler = (handler: BackHandler, active: boolean = true) => {
    const handlerRef = useRef(handler);
    useEffect(() => {
        handlerRef.current = handler;
    }, [handler]);

    useEffect(() => {
        if (!active) return;
        const wrapper = () => handlerRef.current?.();
        return pushBackHandler(wrapper);
    }, [active]);
};
